export type FeeLine = {
  label: string
  amount: number
}

export type TrademarkFeeBreakdown = {
  classCount: number
  serviceLines: FeeLine[]
  officialLines: FeeLine[]
  subtotalServices: number
  subtotalOfficial: number
  total: number
}

export function calculateTrademarkFees({
  goodsAndServices,
  priorityData,
  seniorityData
}: {
  goodsAndServices: any[] | null | undefined
  priorityData: any[] | null | undefined
  seniorityData: any[] | null | undefined
}): TrademarkFeeBreakdown {
  const selectedClassCount = goodsAndServices
    ? new Set(
        goodsAndServices
          .map((entry) => Number(entry.classNumber))
          .filter((classNumber) => Number.isInteger(classNumber) && classNumber > 0)
      ).size
    : 0
  const classCount = Math.max(selectedClassCount, 1)
  const priorityCount = priorityData?.length || 0
  const seniorityCount = seniorityData?.length || 0

  const serviceLines: FeeLine[] = []
  const serviceBase = classCount > 0 ? 280 : 0
  const serviceSecond = classCount >= 2 ? 60 : 0
  const serviceAdditional = classCount >= 3 ? (classCount - 2) * 60 : 0
  const priorityFee = priorityCount * 50
  const seniorityFee = seniorityCount * 50

  if (serviceBase > 0) {
    serviceLines.push({
      label: "Administrative fee for representation and filing",
      amount: serviceBase
    })
  }
  if (serviceSecond > 0) {
    serviceLines.push({ label: "Additional fee for second class", amount: serviceSecond })
  }
  if (serviceAdditional > 0) {
    serviceLines.push({
      label: "Additional fee for third and each additional class",
      amount: serviceAdditional
    })
  }
  if (priorityFee > 0) {
    serviceLines.push({
      label: priorityCount > 1 ? `Claiming priority (${priorityCount}x)` : "Claiming priority",
      amount: priorityFee
    })
  }
  if (seniorityFee > 0) {
    serviceLines.push({
      label: seniorityCount > 1 ? `Claiming seniority (${seniorityCount}x)` : "Claiming seniority",
      amount: seniorityFee
    })
  }

  const officialLines: FeeLine[] = []
  const officialBase = classCount > 0 ? 850 : 0
  const officialSecond = classCount >= 2 ? 50 : 0
  const officialAdditional = classCount >= 3 ? (classCount - 2) * 150 : 0

  if (officialBase > 0) {
    officialLines.push({
      label: "Official fee for EU Trademark (1 class)",
      amount: officialBase
    })
  }
  if (officialSecond > 0) {
    officialLines.push({ label: "Official fee for second class", amount: officialSecond })
  }
  if (officialAdditional > 0) {
    officialLines.push({
      label: "Official fee for third and each additional class",
      amount: officialAdditional
    })
  }

  const subtotalServices = serviceLines.reduce((total, line) => total + line.amount, 0)
  const subtotalOfficial = officialLines.reduce((total, line) => total + line.amount, 0)

  return {
    classCount,
    serviceLines,
    officialLines,
    subtotalServices,
    subtotalOfficial,
    total: subtotalServices + subtotalOfficial
  }
}

export function calculateDesignFeeBreakdown(
  designs: Array<{
    payload?: { priorities?: unknown[] }
    priorities?: unknown[]
  }>,
  applicationPriorityCount = 0
) {
  const designCount = Math.max(designs.length || 1, 1)
  const additionalDesignCount = Math.max(designCount - 1, 0)
  const serviceLines: FeeLine[] = [
    { label: "Representation fee for one design", amount: 375 }
  ]

  if (additionalDesignCount > 0) {
    serviceLines.push({
      label: `Additional designs (${additionalDesignCount})`,
      amount: additionalDesignCount * 85
    })
  }
  const officialLines: FeeLine[] = [
    { label: "Official fee for one design", amount: 350 }
  ]

  if (additionalDesignCount > 0) {
    officialLines.push({
      label: `Official fee for additional designs (${additionalDesignCount})`,
      amount: additionalDesignCount * 125
    })
  }

  const subtotalServices = serviceLines.reduce((sum, line) => sum + line.amount, 0)
  const subtotalOfficial = officialLines.reduce((sum, line) => sum + line.amount, 0)

  return {
    serviceLines,
    officialLines,
    subtotalServices,
    subtotalOfficial,
    total: subtotalServices + subtotalOfficial
  }
}

export function calculateDesignFees(
  designs: Array<{
    payload?: { priorities?: unknown[] }
    priorities?: unknown[]
  }>,
  applicationPriorityCount = 0
) {
  const fees = calculateDesignFeeBreakdown(designs, applicationPriorityCount)
  return [...fees.serviceLines, ...fees.officialLines]
}
